import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

const Step3 = ({
  // Data props
  countries,
  states,
  underwriterOptions,
  howDidYouHearItems,

  // State values
  agencyId,
  underwriter,
  verificationData,
  selectedHowDidYouHear,
  howDidYouHearOther,
  marketingContactName,
  marketingContactPhone,
  marketingContactEmail,
  hasTitle,
  submitButtonText = "Create Profile",
  successMessage = "Registration Successful!",
  successDescription = "Your account has been created successfully. You should receive a confirmation email shortly with further instructions.",
  isStewartEmployee,
  isMemberProfile = false,

  // Error states (using centralized errors object)
  errors,

  // Handler functions
  handleCompanyAgencyIdChange,
  handleCompanyUnderwriterChange,
  clearFieldError,
  handleHowDidYouHearChange,
  handleHowDidYouHearOtherChange,
  handleMarketingContactNameChange,
  handleMarketingContactPhoneChange,
  handleMarketingContactEmailChange,

  // Display control
  isVisible = true,
  stepNumber = 3,
  totalSteps = 3,

  // Submit handling
  submitSuccess,
  submitError,
  isSubmitting,
  dataSourceId,
  googleSiteKey,
}) => {
  // Track the selected country to conditionally require state field
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [userIsInUS, setUserIsInUS] = useState(true);

  // Update state requirement when country changes
  useEffect(() => {
    setUserIsInUS(selectedCountry === "US");
  }, [selectedCountry]);

  // Handle country change
  const handleCountryChange = e => {
    setSelectedCountry(e.target.value);
    clearFieldError("country");
  };

  if (!isVisible) {
    return null;
  }

  return (
    <fieldset
      className={`registration-form__step registration-form__step-3 ${!isVisible ? "registration-form__step--hidden" : ""}`}
      id="step-3"
      hidden={!isVisible}
      aria-describedby="step-3-description"
    >
      {hasTitle && (
        <legend className="registration-form__title">
          Step {stepNumber} of {totalSteps}: Company Information
        </legend>
      )}

      <p
        className="registration-form__description sr-only"
        id="step-3-description"
      >
        Please review and complete your company information to finish the
        registration process.
      </p>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="company-agency-id" className="registration-form__label">
          Agency ID{" "}
          {!isStewartEmployee && !isMemberProfile && (
            <span className="registration-form__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
        <input
          type="text"
          id="company-agency-id"
          name="company_agency_id"
          className="registration-form__input"
          onChange={handleCompanyAgencyIdChange}
          value={agencyId || ""}
          readOnly
          disabled
          required={!isStewartEmployee && !isMemberProfile}
        />
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label
          htmlFor="company-underwriter"
          className="registration-form__label"
        >
          Underwriter{" "}
          {!isStewartEmployee && !isMemberProfile && (
            <span className="registration-form__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
        <select
          id="company-underwriter"
          name="company_underwriter"
          className="registration-form__select"
          value={underwriter || ""}
          onChange={handleCompanyUnderwriterChange}
          disabled={!!underwriter || isStewartEmployee || isMemberProfile}
          required={!isStewartEmployee && !isMemberProfile}
        >
          <option value="" disabled>
            — Select an underwriter —
          </option>
          {underwriterOptions &&
            underwriterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
        </select>
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="companyname" className="registration-form__label">
          Company{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="companyname"
          name="companyname"
          className="registration-form__input"
          defaultValue={verificationData?.companyName || ""}
          required
        />
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="streetaddress1" className="registration-form__label">
          Address 1{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="streetaddress1"
          name="streetaddress1"
          className="registration-form__input"
          defaultValue={verificationData?.streetAddress1 || ""}
          required
        />
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="job-title" className="registration-form__label">
          Job Title{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="job-title"
          name="job_title"
          className={`registration-form__input ${errors.job_title ? "registration-form__input--error" : ""}`}
          aria-invalid={errors.job_title ? "true" : "false"}
          required
          defaultValue={verificationData?.jobTitle || ""}
          onChange={() => clearFieldError("job_title")}
        />
        <div
          id="job-title-error"
          className="registration-form__error"
          hidden={!errors.job_title}
        >
          {errors.job_title}
        </div>
      </div>

      <div className="registration-form__field">
        <label htmlFor="streetaddress2" className="registration-form__label">
          Address 2
        </label>
        <input
          type="text"
          id="streetaddress2"
          name="streetaddress2"
          className="registration-form__input"
          defaultValue={verificationData?.streetAddress2 || ""}
        />
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="phone" className="registration-form__label">
          Phone{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          className={`registration-form__input ${errors.phone ? "registration-form__input--error" : ""}`}
          defaultValue={verificationData?.phoneNumber || ""}
          aria-invalid={errors.phone ? "true" : "false"}
          required
          onChange={() => clearFieldError("phonenumber")}
        />
        <div
          id="phonenumber-error"
          className="registration-form__error"
          hidden={!errors.phonenumber}
        >
          {errors.phonenumber}
        </div>
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="country" className="registration-form__label">
          Country{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <select
          id="country"
          name="country"
          className={`registration-form__select ${errors.country ? "registration-form__select--error" : ""}`}
          aria-invalid={errors.country ? "true" : "false"}
          required
          onChange={handleCountryChange}
          value={selectedCountry}
        >
          <option value="" disabled>
            — Select a country —
          </option>
          {countries &&
            countries.map(country => (
              <option key={country.value} value={country.value}>
                {country.name}
              </option>
            ))}
        </select>
        <div
          id="country-error"
          className="registration-form__error"
          hidden={!errors.country}
        >
          {errors.country}
        </div>
      </div>

      <div className="registration-form__field">
        <label htmlFor="fax" className="registration-form__label">
          Fax
        </label>
        <input
          type="tel"
          id="fax"
          name="fax"
          className="registration-form__input"
          defaultValue={verificationData?.fax || ""}
        />
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="city" className="registration-form__label">
          City
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="city"
          name="city"
          className="registration-form__input"
          defaultValue={verificationData?.city || ""}
          required
        />
      </div>

      <div
        className={`registration-form__field ${userIsInUS ? "registration-form__field--required" : ""}`}
      >
        <label htmlFor="addressstate" className="registration-form__label">
          State{" "}
          {userIsInUS && (
            <span className="registration-form__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
        <select
          id="addressstate"
          name="addressstate"
          className="registration-form__select"
          defaultValue={verificationData?.addressState || ""}
          key={verificationData?.addressState || "default"}
          required={userIsInUS}
        >
          <option value="" disabled>
            — Select a state —
          </option>
          {states &&
            states.map(state => (
              <option key={state.value} value={state.value}>
                {state.name}
              </option>
            ))}
        </select>
      </div>

      <div
        className={`registration-form__field ${userIsInUS ? "registration-form__field--required" : ""}`}
      >
        <label htmlFor="zip" className="registration-form__label">
          Zip{" "}
          {userIsInUS && (
            <span className="registration-form__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
        <input
          type="text"
          id="zip"
          name="zip"
          className={`registration-form__input ${errors.zip ? "registration-form__input--error" : ""}`}
          defaultValue={verificationData?.zipCode || ""}
          aria-invalid={errors.zip ? "true" : "false"}
          required={userIsInUS}
          onChange={() => clearFieldError("zipcode")}
        />
        <div
          id="zipcode-error"
          className="registration-form__error"
          hidden={!errors.zipcode}
        >
          {errors.zipcode}
        </div>
      </div>

      <div className="registration-form__field">
        <fieldset className="registration-form__fieldset">
          <legend className="registration-form__label">
            How did you hear about us?
          </legend>
          <hr />
          <div className="registration-form__hint registration-form__hint-how">
            You may select more than one option below
          </div>
          {howDidYouHearItems &&
            howDidYouHearItems.map(item => (
              <div
                key={item.value}
                className="registration-form__checkbox-wrapper"
              >
                <input
                  type="checkbox"
                  id={`how-did-you-hear-${item.value}`}
                  name="how_did_you_hear"
                  value={item.value}
                  className="registration-form__checkbox"
                  checked={selectedHowDidYouHear.includes(item.value)}
                  onChange={e =>
                    handleHowDidYouHearChange(item.value, e.target.checked)
                  }
                />
                <label
                  htmlFor={`how-did-you-hear-${item.value}`}
                  className="registration-form__checkbox-label"
                >
                  {item.name}
                </label>
              </div>
            ))}

          <div className="registration-form__field">
            <label
              htmlFor="how-did-you-hear-other"
              className="registration-form__label registration-form__label-explain"
            >
              Please explain:
            </label>
            <textarea
              id="how-did-you-hear-other"
              name="how-did-you-hear-other"
              className="registration-form__textarea"
              value={howDidYouHearOther}
              onChange={handleHowDidYouHearOtherChange}
              rows="3"
            />
          </div>
        </fieldset>
      </div>

      <div className="registration-form__field">
        <fieldset className="registration-form__fieldset">
          <legend className="registration-form__label">
            Marketing Contact
          </legend>
          <hr />
          <div className="registration-form__hint">
            Who is responsible for marketing in your company?
          </div>

          <div className="registration-form__field">
            <label
              htmlFor="marketing-contact-name"
              className="registration-form__label"
            >
              Name
            </label>
            <input
              type="text"
              id="marketing-contact-name"
              name="marketing_contact_name"
              className="registration-form__input"
              defaultValue={marketingContactName}
              onChange={handleMarketingContactNameChange}
            />
          </div>

          <div className="registration-form__field">
            <label
              htmlFor="marketing-contact-phone"
              className="registration-form__label"
            >
              Phone
            </label>
            <input
              type="tel"
              id="marketing-contact-phone"
              name="marketing_contact_phone"
              className="registration-form__input"
              value={marketingContactPhone}
              onChange={handleMarketingContactPhoneChange}
            />
          </div>

          <div className="registration-form__field">
            <label
              htmlFor="marketing-contact-email"
              className="registration-form__label"
            >
              Email
            </label>
            <input
              type="email"
              id="marketing-contact-email"
              name="marketing_contact_email"
              className="registration-form__input"
              value={marketingContactEmail}
              onChange={handleMarketingContactEmailChange}
            />
          </div>
        </fieldset>
      </div>

      <div className="registration-form__submit">
        {submitSuccess && (
          <div className="registration-form__success" role="alert">
            {submitSuccess && (
              <div className="registration-form__success" role="alert">
                <h3>{successMessage}</h3>
                {successDescription && <p>{successDescription}</p>}
              </div>
            )}
          </div>
        )}

        {submitError && (
          <div className="registration-form__error form-error" role="alert">
            {submitError}
          </div>
        )}

        <input type="hidden" name="datasource_id" value={dataSourceId || ""} />
        <input
          type="hidden"
          name="google_site_key"
          value={googleSiteKey || ""}
        />

        <button
          type="submit"
          className="registration-form__button btn--primary-large"
          id="create-profile-btn"
          disabled={isSubmitting}
        >
          {submitSuccess
            ? "Registration Complete"
            : isSubmitting
              ? "Processing..."
              : submitButtonText}
        </button>
      </div>
    </fieldset>
  );
};

Step3.propTypes = {
  // Data props
  countries: PropTypes.array,
  states: PropTypes.array,
  underwriterOptions: PropTypes.array,
  howDidYouHearItems: PropTypes.array,

  // State values
  agencyId: PropTypes.string,
  underwriter: PropTypes.string,
  verificationData: PropTypes.object,
  selectedHowDidYouHear: PropTypes.array,
  howDidYouHearOther: PropTypes.string,
  marketingContactName: PropTypes.string,
  marketingContactPhone: PropTypes.string,
  marketingContactEmail: PropTypes.string,
  hasTitle: PropTypes.bool,
  submitButtonText: PropTypes.string,
  successMessage: PropTypes.string,
  successDescription: PropTypes.string,

  // Error states (using centralized errors object)
  errors: PropTypes.object,

  // Handler functions
  handleCompanyAgencyIdChange: PropTypes.func.isRequired,
  handleCompanyUnderwriterChange: PropTypes.func.isRequired,
  clearFieldError: PropTypes.func.isRequired,
  handleHowDidYouHearChange: PropTypes.func.isRequired,
  handleHowDidYouHearOtherChange: PropTypes.func.isRequired,
  handleMarketingContactNameChange: PropTypes.func.isRequired,
  handleMarketingContactPhoneChange: PropTypes.func.isRequired,
  handleMarketingContactEmailChange: PropTypes.func.isRequired,

  // Display control
  isVisible: PropTypes.bool,
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,

  // Submit handling
  submitSuccess: PropTypes.bool,
  submitError: PropTypes.string,
  isSubmitting: PropTypes.bool,
  dataSourceId: PropTypes.string,
  googleSiteKey: PropTypes.string,
};

export default Step3;
